import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api-client";
import { ALL_CAMPAIGNS, type AimfoxCampaign, type NotificationRecipient } from "../../lib/types";
import { Button } from "../ui/button";
import { Icon } from "../ui/icon";
import { RecipientForm } from "./recipient-form";

type View = { kind: "list" } | { kind: "add" } | { kind: "edit"; recipient: NotificationRecipient };

// Right-panel content for the Settings dialog's one (so far) left-nav
// section: "Notification". Owns its own data (recipients + the live campaign
// list, for the picker) and the list/add/edit view switch described in the
// spec — clicking a listed email opens the same form "Add email" uses,
// pre-filled.
//
// Recipients and campaigns are fetched independently, NOT via one Promise.all
// (that was the original shape, and gating the dialog's first paint on both
// is exactly what made it slow to open — see getCampaignsPicker's own
// comment). Recipients (a small ops_v2 join) resolve almost immediately and
// are all the list view needs; campaigns are only needed once the operator
// actually opens Add/Edit, by which point the picker call has usually
// already finished in the background.
export function NotificationSettingsPanel() {
  const [recipients, setRecipients] = useState<NotificationRecipient[] | null>(null);
  const [campaigns, setCampaigns] = useState<AimfoxCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: "list" });

  useEffect(() => {
    let cancelled = false;
    api.getNotificationSettings()
      .then((settings) => {
        if (!cancelled) setRecipients(settings.recipients);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
      });
    api.getCampaignsPicker()
      .then((picker) => {
        if (!cancelled) setCampaigns(picker.campaigns);
      })
      .catch(() => {
        // Non-fatal for the panel as a whole — the recipients list still
        // renders; only the campaign picker inside Add/Edit is affected, and
        // CampaignMultiSelect already handles an empty campaign list.
      })
      .finally(() => {
        if (!cancelled) setCampaignsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function summarize(recipient: NotificationRecipient): string {
    const n = recipient.campaignPrefs.length;
    const hasAll = recipient.campaignPrefs.some((p) => p.campaignId === ALL_CAMPAIGNS);
    const campaignWord = n === 1 ? "campaign" : "campaigns";
    return hasAll && n === 1 ? "All campaigns" : `${n} ${campaignWord}`;
  }

  if (loadError) {
    return <p className="font-body-sm text-body-sm text-error">{loadError}</p>;
  }

  if (recipients === null) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant flex items-center gap-spacing-2xs">
        <Icon name="progress_activity" className="text-[16px] animate-spin" />
        Loading…
      </p>
    );
  }

  if (view.kind === "add") {
    return (
      <RecipientForm
        mode="create"
        campaigns={campaigns}
        campaignsLoading={campaignsLoading}
        onCancel={() => setView({ kind: "list" })}
        onDeleted={() => setView({ kind: "list" })}
        onSaved={(recipient) => {
          setRecipients([...recipients, recipient]);
          setView({ kind: "list" });
        }}
      />
    );
  }

  if (view.kind === "edit") {
    return (
      <RecipientForm
        mode="edit"
        recipient={view.recipient}
        campaigns={campaigns}
        campaignsLoading={campaignsLoading}
        onCancel={() => setView({ kind: "list" })}
        onDeleted={() => {
          setRecipients(recipients.filter((r) => r.id !== view.recipient.id));
          setView({ kind: "list" });
        }}
        onSaved={(recipient) => {
          setRecipients(recipients.map((r) => (r.id === recipient.id ? recipient : r)));
          setView({ kind: "list" });
        }}
      />
    );
  }

  return (
    <div className="space-y-spacing-base">
      <div className="flex items-center justify-between">
        <h2 className="font-headline-sm text-headline-sm text-on-surface">Email notifications</h2>
        <Button variant="primary" onClick={() => setView({ kind: "add" })}>
          <Icon name="add" className="text-[16px]" />
          Add email
        </Button>
      </div>
      {recipients.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          No one is set up to receive email notifications yet.
        </p>
      ) : (
        // Its own capped scroll area (not just relying on the dialog's outer
        // overflow-y-auto) — max-h-48 fits at least 3 rows at this row
        // height/gap before scrolling, so "Email notifications" + Add email
        // stay visible above it instead of scrolling away with the list.
        <ul className="space-y-spacing-2xs max-h-48 overflow-y-auto pr-spacing-3xs">
          {recipients.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setView({ kind: "edit", recipient: r })}
                className="w-full flex items-center justify-between gap-spacing-sm px-spacing-md py-spacing-sm bg-surface-container-low hover:bg-surface-container rounded-xl text-left transition-colors"
              >
                <span className="font-body-md text-body-md text-on-surface truncate">{r.email}</span>
                <span className="font-label-sm text-label-sm text-on-surface-variant shrink-0">{summarize(r)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
